import MediaKpiModule from '@/models/MediaKpiModule'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import numeral from 'numeral'
import moment from 'moment'
import SelectOption from '@/models/interface/SelectOption'
import { clone } from 'lodash'
import { FILTERS } from '../../helpers'
import YearMonthQuarterColumnToggle from '../../filters/YearMonthQuarterColumnToggle'

const cpm_total = (primary_key: string, secondary_key: string, data: any[]) => {
  const primary = data.reduce((acc, item) => {
    const value = item[primary_key] || 0
    return acc + value
  }, 0)
  const secondary = data.reduce((acc, item) => {
    const value = item[secondary_key] || 0
    return acc + value
  }, 0)

  return primary && secondary ? (primary / secondary) * 1000 : 0
}
export default class SalesChannelProfitWidget extends SystemDashboardWidget {
  public title: string = 'Sales Channel'

  public key: string = 'sales-channel-profit'

  public view: string = 'table-group'

  public noPagination: boolean = true

  public allowAll: boolean = true

  public noTotal: boolean = false

  public bordered: boolean = true

  public loaderSize: string = '350px'

  public flattenPayload: boolean = true

  public perPage: any = 'all'

  public _show: any = null

  public refreshInternalTableFields: any = {
    fields: [],
  }

  public internalProps: any = {
    show_yesterday: true,
    show_mtd: true,
    show_quarter: true,
    show_year: true,
    show_impressions: true,
    show_revenue: true,
    show_cpm: true,
    show_profit: true,
  }

  public rightCol: any = [
    new YearMonthQuarterColumnToggle({
      options: [
        new SelectOption('Impressions', 'show_impressions'),
        new SelectOption('Revenue/Cost', 'show_revenue'),
        new SelectOption('RPM/CPM', 'show_cpm'),
        new SelectOption('Profit', 'show_profit'),
      ],
    }),
  ]

  // public leftCol: any = [
  //   new YearMonthQuarterColumnToggle({
  //     options: [
  //       new SelectOption('Yesterday', 'show_yesterday'),
  //       new SelectOption('MTD', 'show_mtd'),
  //       new SelectOption('Quarter', 'show_quarter'),
  //       new SelectOption('Year', 'show_year'),
  //     ],
  //   }),
  // ]

  public subFields: any = [
    {
      key: 'label',
      label: '',
      show: true,
      footer: { label: 'Total' },
      thClass: 'align-middle text-left',
      cellClass: 'align-middle',
    },
    {
      key: 'yesterday_impressions',
      label: 'Impressions',
      show: true,
      type: 'numeral',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'yesterday_revenue',
      label: 'Revenue',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'yesterday_rpm',
      label: 'RPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'billable_impressions', data),
    },
    {
      key: 'yesterday_erpm',
      label: 'eRPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
    },
    {
      key: 'yesterday_cost',
      label: 'Cost (wo/MO)',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'yesterday_cpm',
      label: 'CPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('cost', 'impressions', data),
    },
    {
      key: 'yesterday_profit',
      label: 'Profit $/%',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      htmlConcat: (item: any) => this.htmlComponentConcat(item, 'yesterday'),
    },

    {
      key: 'mtd_impressions',
      label: 'Impressions',
      show: true,
      type: 'numeral',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'mtd_revenue',
      label: 'Revenue',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'mtd_rpm',
      label: 'RPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'billable_impressions', data),
    },
    {
      key: 'mtd_erpm',
      label: 'eRPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
    },
    {
      key: 'mtd_cost',
      label: 'Cost (w/MO)',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'mtd_cpm',
      label: 'CPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('cost', 'impressions', data),
    },
    {
      key: 'mtd_profit',
      label: 'Profit $/%',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      htmlConcat: (item: any) => this.htmlComponentConcat(item, 'mtd'),
    },

    {
      key: 'quarter_impressions',
      label: 'Impressions',
      show: true,
      type: 'numeral',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'quarter_revenue',
      label: 'Revenue',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'quarter_rpm',
      label: 'RPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'billable_impressions', data),
    },
    {
      key: 'quarter_erpm',
      label: 'eRPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
    },
    {
      key: 'quarter_cost',
      label: 'Cost (w/MO)',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'quarter_cpm',
      label: 'CPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('cost', 'impressions', data),
    },
    {
      key: 'quarter_profit',
      label: 'Profit $/%',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      htmlConcat: (item: any) => this.htmlComponentConcat(item, 'quarter'),
    },

    {
      key: 'year_impressions',
      label: 'Impressions',
      show: true,
      type: 'numeral',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'year_revenue',
      label: 'Revenue',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'year_rpm',
      label: 'RPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'billable_impressions', data),
    },
    {
      key: 'year_erpm',
      label: 'eRPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('revenue', 'impressions', data),
    },
    {
      key: 'year_cost',
      label: 'Cost (w/MO)',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
    },
    {
      key: 'year_cpm',
      label: 'CPM',
      show: false,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      customTotal: (data: any) => cpm_total('cost', 'impressions', data),
    },
    {
      key: 'year_profit',
      label: 'Profit $/%',
      show: true,
      type: 'currency',
      tdClass: 'align-middle text-center',
      thClass: 'align-middle text-center',
      htmlConcat: (item: any) => this.htmlComponentConcat(item, 'year'),
    },
  ]

  public fields: any = [
    {
      colspan: 1,
      key: 'label',
      label: '',
      sortable: false,
      filter: false,
      show: true,
      thClass: 'p-0 align-middle text-center ',
      tdClass: 'align-middle text-center p-0 text-capitalize',
      thStyle: 'min-width:200px!important; width:400px!important',
    },
    {
      colspan: 4,
      key: 'yesterday',
      label: 'Yesterday',
      sortable: false,
      filter: false,
      show: true,
      thClass: 'p-0  align-middle text-center ',
      tdClass: 'align-middle text-center p-0 text-capitalize',
      thStyle: 'min-width:200px!important; width:200px!important',
    },
    {
      colspan: 4,
      key: 'mtd',
      label: 'MTD',
      sortable: false,
      filter: false,
      show: true,
      thClass: 'p-0  align-middle text-center ',
      tdClass: 'align-middle text-center p-0 text-capitalize',
      thStyle: 'min-width:200px!important; width:200px!important',
    },
    {
      colspan: 4,
      key: 'quarter',
      label: 'Quarter (recognized + estimate MTD)',
      sortable: false,
      filter: false,
      show: true,
      thClass: 'p-0  align-middle text-center',
      tdClass: 'align-middle text-center p-0 text-capitalize',
      thStyle: 'min-width:200px!important; width:200px!important',
    },
    {
      colspan: 4,
      key: 'year',
      label: 'Year (recognized + estimate MTD)',
      sortable: false,
      filter: false,
      show: true,
      thClass: 'p-0  align-middle text-center ',
      tdClass: 'align-middle text-center p-0 text-capitalize',
      thStyle: 'min-width:200px!important; width:200px!important',
    },
  ]

  public size: any = {
    sm: 12,
    md: 12,
    lg: 12,
  }

  public filters: any = {
    region: 'all',
    product: 'all',
  }

  public internalFilters: any = {
    ...FILTERS.YEAR,
  }

  public get filterToQuery() {
    let query = []

    if (this.filters.product !== 'all') {
      query.push(`product:${this.filters.product}`)
    }

    if (this.filters.region !== 'all') {
      query.push(`region:${this.filters.region}`)
    }

    if (this.filters.special_group !== 'all') {
      query.push(`special_group:${this.filters.special_group}`)
    }

    query.push(`start:${moment(this.filters.start).format('YYYY-MM-DD')}`)
    query.push(`end:${moment(this.filters.end).format('YYYY-MM-DD')}`)

    return query
  }

  public dataSourceRequest: any = null

  public cellSlots: any[] = []

  public show() {
    if (this._show != null) {
      return this._show(this)
    }
    return true
  }

  public htmlComponentConcat(item: any, key: string) {
    const foundItem = item[key].find((f: any) => f.key === 'profit')
    const profit = numeral(foundItem.profit * 0.01).format('0.0%')
    let _class = foundItem.profit > 0 ? 'text-primary' : 'text-danger'
    if (foundItem.profit === 0) {
      _class = ''
    }
    return `  <sup class="ml-1 ${foundItem.profit === 0 ? 'd-none' : ''}">
                  <span class="${_class} font-weight-bold">${profit}</span>
                  <sup class="${_class}" style="font-size:10px"> 
                      ${foundItem.profit > 0 ? '▴' : '▾'} 
                  </sup>
                </sup>
            `
  }

  constructor(data: any = null) {
    super()
    if (data) {
      if (data.key) this.key = data.key
      if (data.view) this.view = data.view
      if (data.size) this.size = data.size
      if (data.title) this.title = data.title
      if (data.fields) this.fields = data.fields
      if (data.subFields) this.subFields = data.subFields
      if (data.noTotal) this.noTotal = data.noTotal
      if (data.filters) this.filters = data.filters
      if (data.allowAll) this.allowAll = data.allowAll
      if (data.bordered) this.bordered = data.bordered
      if (data.cellSlots) this.cellSlots = data.cellSlots
      if (data.loaderSize) this.loaderSize = data.loaderSize
      if (data.noPagination) this.noPagination = data.noPagination
      if (data.internalProps) this.internalProps = data.internalProps
      if (data.flattenPayload) this.flattenPayload = data.flattenPayload
      if (data.internalFilters) this.internalFilters = data.internalFilters
      if (data.rightCol) this.rightCol = data.rightCol
      if (data.dataSourceRequest) this.dataSourceRequest = data.dataSourceRequest
      if (data.internalQueryPush) this.internalQuery.push(data.internalQueryPush)
      if (data.firstLabel) {
        this.subFields[0].label = data.firstLabel
      }
      if (data._show) {
        this._show = data._show
      }

      if (data.conditionalCellClass) {
        let index = this.subFields.findIndex((s: any) => s.key === 'label')
        this.subFields[index].conditionalCellClass = data.conditionalCellClass
      }
    }
  }

  public get payload() {
    const query = [...new Set([...this.query, ...this.internalQuery, ...this.filterToQuery])]

    return {
      query,
      ...this.pagination,
    }
  }

  public dataSource() {
    if (this.dataSourceRequest) {
      const res = this.dataSourceRequest(this.payload)
      return res.then((r: any) => {
        this.records = r.records

        if (r.result) return r.result

        return r.data
      })
    }

    let request = new MediaKpiModule()
    return request.paginate(this.payload).then((r: any) => {
      this.records = r.records

      if (r.result) return r.result

      return r.data
    })
  }

  public showFields(_: SystemDashboardWidget, fields: any) {
    return clone(fields)
      .map((field: any) => {
        if (field.key.startsWith('yesterday')) {
          field.show = this.internalProps.show_yesterday
        } else if (field.key.startsWith('mtd')) {
          field.show = this.internalProps.show_mtd
        } else if (field.key.startsWith('quarter')) {
          field.show = this.internalProps.show_quarter
        } else if (field.key.startsWith('year')) {
          field.show = this.internalProps.show_year
        }

        if (field.key === 'label') {
          return field
        }
        let count = 0
        if (this.internalProps.show_impressions) {
          if (this.internalProps.single_control) {
            count++
          } else {
            count += 1
          }
        }
        if (this.internalProps.show_revenue) {
          if (this.internalProps.single_control) {
            count++
          } else {
            count += 3
          }
        }
        if (this.internalProps.show_cpm) {
          if (this.internalProps.single_control) {
            count++
          } else {
            count += 3
          }
        }
        if (this.internalProps.show_profit) {
          if (this.internalProps.single_control) {
            count++
          } else {
            count += 1
          }
        }

        field.colspan = count

        return field
      })
      .filter((field: any) => field.show !== false)
  }

  public showSubFields(widget: SystemDashboardWidget, fields: any) {
    return clone(fields)
      .map((sub: any) => {
        if (sub.key.endsWith('_impressions')) {
          sub.show = this.internalProps.show_impressions
        } else if (sub.key.endsWith('_revenue') || sub.key.endsWith('_cost')) {
          sub.show = this.internalProps.show_revenue
        } else if (
          sub.key.endsWith('_cpm')
          || sub.key.endsWith('_erpm')
          || sub.key.endsWith('_rpm')
        ) {
          sub.show = this.internalProps.show_cpm
        } else if (sub.key.endsWith('_profit')) {
          sub.show = this.internalProps.show_profit
        } else {
          sub.show = true
        }

        // TODO: This is causing an infinite loop, investigate!
        // if (sub.key.startsWith('yesterday_')) {
        //   sub.show = this.internalProps.show_yesterday === true
        // } else if (sub.key.startsWith('mtd_')) {
        //   sub.show = this.internalProps.show_mtd === true
        // } else if (sub.key.startsWith('quarter_')) {
        //   sub.show = this.internalProps.show_quarter === true
        // } else if (sub.key.startsWith('year_')) {
        //   sub.show = this.internalProps.show_year === true
        // }

        return sub
      })
      .filter((field: any) => field.show !== false)
  }
}
